import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TrophyIcon, ClockIcon, BoltIcon, TicketIcon, CalendarIcon } from '@heroicons/react/24/outline';

interface LeaderboardEntry {
  operator: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  ticketsProcessed: number;
  avgResolutionTime: number; // секунды
  avgResponseTime: number; // секунды
  // CSAT: avg of 1-5 star ratings the operator received in the period.
  // `null` when no client rated their work — render as "—".
  avgCsat: number | null;
  csatRatingsCount: number;
  csatResponseRate: number | null;
}

// Локальная дата в 'YYYY-MM-DD' БЕЗ ухода в UTC (toISOString откатывал бы
// локальную полночь на предыдущий день, из-за чего «1 сентября» превращалось
// в «31 августа» и весь день 31.08 попадал в сентябрьский период).
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const LeaderboardPage: React.FC = () => {
  // Инициализация с текущим месяцем по умолчанию
  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: ymd(start),
      end: ymd(end)
    };
  };

  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getCurrentMonthRange());

  useEffect(() => {
    fetchLeaderboard();
  }, [dateRange.start, dateRange.end]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/reports/leaderboard', {
        params: {
          startDate: dateRange.start,
          endDate: dateRange.end
        }
      });
      if (response.data.success) {
        setLeaderboard(response.data.data.leaderboard || []);
      }
    } catch (error: any) {
      toast.error('Ошибка загрузки лидерборда');
    } finally {
      setLoading(false);
    }
  };

  const setCurrentMonth = () => {
    setDateRange(getCurrentMonthRange());
  };

  const setCurrentDay = () => {
    const today = ymd(new Date());
    setDateRange({ start: today, end: today });
  };

  const setCurrentWeek = () => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // Начало недели (воскресенье)
    const end = new Date(start);
    end.setDate(start.getDate() + 6); // Конец недели

    setDateRange({
      start: ymd(start),
      end: ymd(end)
    });
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0 сек';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    } else if (minutes > 0) {
      return `${minutes}м ${secs}с`;
    } else {
      return `${secs}с`;
    }
  };

  const getMedalIcon = (position: number) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return null;
  };

  const getMedalColor = (position: number) => {
    if (position === 1) return 'bg-yellow-500/20 border-yellow-500/50';
    if (position === 2) return 'bg-gray-300/20 border-gray-400/50';
    if (position === 3) return 'bg-orange-500/20 border-orange-500/50';
    return 'bg-card border-border';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Загрузка лидерборда...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <TrophyIcon className="h-6 w-6" />
          Лидерборд операторов
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Рейтинг операторов по эффективности работы
        </p>
        {dateRange.start && dateRange.end && (
          <p className="mt-1 text-xs text-muted-foreground">
            Период: {new Date(dateRange.start).toLocaleDateString('ru-RU')} - {new Date(dateRange.end).toLocaleDateString('ru-RU')}
          </p>
        )}
      </div>

      {/* Выбор диапазона дат */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-muted-foreground" />
            <label className="text-sm font-medium text-foreground">
              Период:
            </label>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-xs text-muted-foreground mb-1">
                Дата начала
              </label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>
            
            <div className="flex-1">
              <label className="block text-xs text-muted-foreground mb-1">
                Дата окончания
              </label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={setCurrentDay}
            >
              Сегодня
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={setCurrentWeek}
            >
              Текущая неделя
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={setCurrentMonth}
            >
              Текущий месяц
            </Button>
          </div>
        </div>
      </div>

      {/* Лидерборд */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {leaderboard.length === 0 ? (
          <div className="p-12 text-center">
            <TrophyIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Нет данных за выбранный период</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Место</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Оператор</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    <TicketIcon className="h-4 w-4 inline mr-1" />
                    Обработано заявок
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    <ClockIcon className="h-4 w-4 inline mr-1" />
                    Среднее время закрытия
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    <BoltIcon className="h-4 w-4 inline mr-1" />
                    Среднее время реакции
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                    ⭐ CSAT
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leaderboard.map((entry, index) => {
                  const position = index + 1;
                  const medal = getMedalIcon(position);
                  
                  return (
                    <tr
                      key={entry.operator.id}
                      className={`hover:bg-muted/30 ${getMedalColor(position)}`}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {medal && <span className="text-2xl">{medal}</span>}
                          <span className="text-lg font-semibold text-foreground">
                            {position}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-foreground">
                          {entry.operator.firstName} {entry.operator.lastName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          @{entry.operator.username}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <TicketIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-foreground font-semibold">
                            {entry.ticketsProcessed}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-foreground">
                            {formatTime(entry.avgResolutionTime)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <BoltIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-foreground">
                            {formatTime(entry.avgResponseTime)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {entry.avgCsat == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-col">
                            <span className="text-foreground font-semibold">
                              {entry.avgCsat.toFixed(2)} / 5
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {entry.csatRatingsCount} оцен.
                              {entry.csatResponseRate != null && (
                                <> · {(entry.csatResponseRate * 100).toFixed(0)}%</>
                              )}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Статистика */}
      {leaderboard.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-1">Всего операторов</div>
            <div className="text-2xl font-bold text-foreground">{leaderboard.length}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-1">Всего обработано заявок</div>
            <div className="text-2xl font-bold text-foreground">
              {leaderboard.reduce((sum, entry) => sum + entry.ticketsProcessed, 0)}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-1">Среднее время закрытия</div>
            <div className="text-2xl font-bold text-foreground">
              {formatTime(
                Math.round(
                  leaderboard.reduce((sum, entry) => sum + entry.avgResolutionTime, 0) /
                  leaderboard.length
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardPage;

